import { Button, Modal, Label, TextInput, Textarea } from "flowbite-react";
import { useForm } from "react-hook-form";
import { EventStatus } from "@/sharedTypes";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface EventStatusFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: EventStatus) => void;
  defaultValues?: EventStatus;
  mode?: "create" | "edit";
  errorMessage?: string;
}

export default function EventStatusFormDialog({
  open,
  onClose,
  onSubmit,
  defaultValues,
  mode = "create",
  errorMessage,
}: EventStatusFormDialogProps) {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EventStatus>();

  useEffect(() => {
    if (open) {
      reset(
        defaultValues || {
          status: "",
          description: "",
        }
      );
    }
  }, [open, defaultValues, reset]);

  return (
    <Modal show={open} onClose={onClose}>
      <Modal.Header>
        {mode === "edit" ? t("temporalActivity:eventStatus.edit") : t("temporalActivity:eventStatus.create")}
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <div className="mb-2 block">
              <Label htmlFor="status" value={t("temporalActivity:eventStatus.name")} />
            </div>
            <TextInput
              id="status"
              placeholder={t("temporalActivity:eventStatus.namePlaceholder")}
              {...register("status", {
                required: t("temporalActivity:eventStatus.nameRequired") as string,
              })}
              color={errors.status ? "failure" : "gray"}
              helperText={errors.status?.message}
            />
          </div>

          <div>
            <div className="mb-2 block">
              <Label htmlFor="description" value={t("temporalActivity:eventStatus.description")} />
            </div>
            <Textarea
              id="description"
              placeholder={t("temporalActivity:eventStatus.descriptionPlaceholder")}
              {...register("description")}
            />
          </div>

          {errorMessage && <div className="text-sm text-red-500">{errorMessage}</div>}

          <div className="flex justify-end gap-x-2">
            <Button color="gray" onClick={onClose}>
              {t("common:cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {mode === "edit" ? t("common:save") : t("common:create")}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
